/* IntakeQueue — admin worklist for PR-K Free Scan submissions.
   Self-fetches from /api/intake/queue so this tab can drop into the
   admin panel without threading new fetch lifecycles through App.jsx.
   The existing tabs (Users, Licenses, etc.) get their data from the
   parent; that's the long-term shape and a follow-up PR can hoist this
   too once the queue moves beyond soft-preview. */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { P, FC } from "../../shared/constants";
import EmptyState from "../components/EmptyState";
import ErrorState from "../components/ErrorState";
import Skeleton from "../components/Skeleton";
import Chip from "../components/Chip";
import Modal from "../components/Modal";
import { Refresh, Mail } from "../icons";
var E = React.createElement;

/* PR-K.21: date filter options. Values match the server's parseSince()
   shorthand. Empty string = all-time. */
var DATE_OPTIONS = [
  { id: "",     label: "All time" },
  { id: "7d",   label: "Last 7 days" },
  { id: "30d",  label: "Last 30 days" },
  { id: "90d",  label: "Last 90 days" },
  { id: "365d", label: "Last year" }
];

var STATUS_OPTIONS = [
  { id: "", label: "All (active)" },
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "scanning", label: "Scanning…" },
  { id: "scan_failed", label: "Scan failed" },
  { id: "denied", label: "Denied" },
  { id: "contacted", label: "Contacted" },
  { id: "scanned", label: "Scanned" },
  { id: "delivered", label: "Delivered" },
  { id: "archived", label: "Archived" }
];

var SOURCE_OPTIONS = [
  { id: "", label: "All sources" },
  { id: "paris", label: "PiARIS landing" },
  { id: "pi-consulting", label: "Pi Consulting site" }
];

/* Status → chip variant. Matches the semantic palette already used
   elsewhere in the admin panel: amber = waiting, accent = in motion,
   pri = work in progress, emerald = success, slate = filed. */
var STATUS_VARIANT = {
  pending:   "amber",
  /* PR-K.19: approved + denied get distinct chips so they jump out of
     the queue at a glance — pri-violet for "ready to scan", coral-red
     for "won't scan". */
  approved:  "violet",
  denied:    "coral",
  /* PR-K.28: Phase C worker lifecycle. scanning uses accent (in
     motion); scan_failed uses coral (needs attention). */
  scanning:    "accent",
  scan_failed: "coral",
  contacted: "accent",
  scanned:   "pri",
  delivered: "emerald",
  archived:  "slate"
};

/* Available next-state actions per row. Drives the inline action menu so
   the admin doesn't need to memorize the workflow. Each action ships a
   verb so the button label reads naturally. */
/* PR-K.19: pending rows get two primary actions — Approve (which
   Phase C will pick up automatically) and Deny (manual review
   rejected the request). Both flow back to admin-driven cleanup.

   PR-K.22: rows in approved/scanned with a scan session linked also
   gain a SEND REPORT virtual action — surfaced separately from the
   status-transition buttons so the email-out side stays distinct from
   the workflow state machine.

   PR-K.25: "Approve & Scan" → "Approve". Phase C (the actual scan
   automation) isn't shipped yet; the label was overselling. */
var NEXT_ACTIONS = {
  pending:   [
    { status: "approved",  label: "Approve",         primary: true },
    { status: "denied",    label: "Deny",            danger: true,
      prompt: "Reason for denial (optional):" },
    { status: "contacted", label: "Mark contacted" },
    { status: "archived",  label: "Archive" }
  ],
  approved:  [
    { status: "scanned",   label: "Mark scanned" },
    { status: "pending",   label: "Un-approve" },
    { status: "archived",  label: "Archive" }
  ],
  denied:    [
    { status: "pending",   label: "Re-open" },
    { status: "archived",  label: "Archive" }
  ],
  /* PR-K.28: scanning state is normally set/cleared by the Phase C
     worker. Admin overrides exist for the case where a worker died
     silently and the row needs to come back into the queue. */
  scanning:    [
    { status: "approved",  label: "Cancel & re-queue" },
    { status: "scan_failed", label: "Mark failed",  danger: true,
      prompt: "Reason (optional):" },
    { status: "archived",  label: "Archive" }
  ],
  scan_failed: [
    { status: "approved",  label: "Retry",          primary: true },
    { status: "archived",  label: "Archive" }
  ],
  contacted: [{ status: "scanned",   label: "Mark scanned"   }, { status: "archived", label: "Archive" }],
  scanned:   [{ status: "delivered", label: "Mark delivered (manual)" }, { status: "archived", label: "Archive" }],
  delivered: [{ status: "archived",  label: "Archive" }],
  archived:  [{ status: "pending",   label: "Restore to pending" }]
};

/* PR-K.22: which statuses can fire the Send Report action. Visible
   only when the row also has a scanSessionId set — otherwise the
   button would 400 with "paste session id first". */
var SEND_REPORT_STATUSES = { approved: 1, scanned: 1, contacted: 1 };

function fmtWhen(iso) {
  if (!iso) return "—";
  /* Compact local-ish format; "2026-06-07 14:23" reads cleaner than ISO. */
  return String(iso).replace("T", " ").slice(0, 16);
}

function buildMailto(row) {
  if (!row || !row.email) return "#";
  var subject = "Your PiARIS free accessibility scan for " + (row.domain || "your site");
  var body = [
    "Hi " + (row.name || "there") + ",",
    "",
    "Thanks for requesting a free accessibility scan of " + (row.domain || "your site") + ".",
    "Attached is your WCAG 2.2 AA report. The top issues are listed first so your team",
    "can start with the highest-impact fixes.",
    "",
    "Happy to walk through the findings on a 30-minute call — just reply with a time that works.",
    "",
    "— Pi Consulting",
    "pi-consulting.us"
  ].join("\n");
  return "mailto:" + encodeURIComponent(row.email)
    + "?subject=" + encodeURIComponent(subject)
    + "&body=" + encodeURIComponent(body);
}

export default function IntakeQueue(props){
  var showToast = props.showToast || function(){};

  var s_items   = useState([]);            var items = s_items[0],   setItems   = s_items[1];
  var s_total   = useState(0);             var total = s_total[0],   setTotal   = s_total[1];
  var s_counts  = useState({});            var counts = s_counts[0], setCounts  = s_counts[1];
  var s_loading = useState(true);          var loading = s_loading[0], setLoading = s_loading[1];
  var s_error   = useState(null);          var error = s_error[0],   setError   = s_error[1];
  var s_status  = useState("");            var status = s_status[0], setStatus  = s_status[1];
  var s_source  = useState("");            var source = s_source[0], setSource  = s_source[1];
  var s_q       = useState("");            var q     = s_q[0],       setQ       = s_q[1];
  var s_busy    = useState({});            var busy  = s_busy[0],    setBusy    = s_busy[1];
  var s_csvBusy = useState(false);         var csvBusy = s_csvBusy[0], setCsvBusy = s_csvBusy[1];
  /* PR-K.21: date filter + bulk select state. since = "" | "7d" | "30d"
     | "90d" | "365d". selected = Set of row ids currently checked. */
  var s_since = useState("");              var since = s_since[0],   setSince   = s_since[1];
  var s_selected = useState({});           var selected = s_selected[0], setSelected = s_selected[1];
  var s_bulkBusy = useState(false);        var bulkBusy = s_bulkBusy[0], setBulkBusy = s_bulkBusy[1];

  /* Detail modal state. detailRow:
       null              = closed
       { id, source }    = opening (initial click; full doc fetching)
       { ...fullDoc }    = open with full server-side detail
     Notes + assignee are edit-buffered so saving them is a single PATCH. */
  var s_detailRow      = useState(null);   var detailRow      = s_detailRow[0],      setDetailRow      = s_detailRow[1];
  var s_detailLoading  = useState(false);  var detailLoading  = s_detailLoading[0],  setDetailLoading  = s_detailLoading[1];
  var s_detailError    = useState(null);   var detailError    = s_detailError[0],    setDetailError    = s_detailError[1];
  var s_detailNotes    = useState("");     var detailNotes    = s_detailNotes[0],    setDetailNotes    = s_detailNotes[1];
  var s_detailAssignee = useState("");     var detailAssignee = s_detailAssignee[0], setDetailAssignee = s_detailAssignee[1];
  var s_detailSaving   = useState(false);  var detailSaving   = s_detailSaving[0],   setDetailSaving   = s_detailSaving[1];
  var s_detailScanSessionId = useState(""); var detailScanSessionId = s_detailScanSessionId[0], setDetailScanSessionId = s_detailScanSessionId[1];
  /* PR-K.28: engagement toggle state — admin classification that
     drives the Phase C worker's dispatch priority. */
  var s_detailEngagement = useState("free");
  var detailEngagement = s_detailEngagement[0], setDetailEngagement = s_detailEngagement[1];
  /* PR-K.32: dispatch mode safety gate. "manual" (default) keeps the
     intake out of the Phase C worker's claim query — admin runs the
     scan in PARIS themselves. "auto" opts the row into the worker
     pool. Admin flips this per-row until the automated path is
     trusted. */
  var s_detailDispatchMode = useState("manual");
  var detailDispatchMode = s_detailDispatchMode[0], setDetailDispatchMode = s_detailDispatchMode[1];
  /* PR-K.22 — per-row Send Report busy tracker. */
  var s_sending  = useState({});           var sending = s_sending[0], setSending = s_sending[1];

  /* Latest-request guard — drop responses from filter changes that
     superseded each other so a slow first fetch can't overwrite a fast
     second one. */
  var reqIdRef = useRef(0);

  var load = useCallback(function(){
    var myId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    var params = [];
    if (status) params.push("status=" + encodeURIComponent(status));
    if (source) params.push("source=" + encodeURIComponent(source));
    if (q)      params.push("q="      + encodeURIComponent(q));
    if (since)  params.push("since="  + encodeURIComponent(since));
    var url = "/api/intake/queue" + (params.length ? "?" + params.join("&") : "");

    fetch(url, { credentials: "include" })
      .then(function(r){
        return r.json().catch(function(){ return {}; }).then(function(b){
          return { status: r.status, body: b };
        });
      })
      .then(function(out){
        if (myId !== reqIdRef.current) return;
        if (out.status >= 200 && out.status < 300) {
          setItems(out.body.items || []);
          setTotal(out.body.total || 0);
          setCounts(out.body.counts || {});
          setLoading(false);
          return;
        }
        setError((out.body && out.body.error) || ("HTTP " + out.status));
        setLoading(false);
      })
      .catch(function(e){
        if (myId !== reqIdRef.current) return;
        setError(String((e && e.message) || e));
        setLoading(false);
      });
  }, [status, source, q, since]);

  /* Mount fetch + refetch when filters change. */
  useEffect(function(){ load(); }, [load]);

  /* PR-K.21: clear selection any time the filter changes — selected
     rows that filter out of view shouldn't ghost in the action bar. */
  useEffect(function(){ setSelected({}); }, [status, source, q, since]);

  function patchStatus(row, nextStatus, extra){
    if (!row || !row.id) return;
    extra = extra || {};
    setBusy(function(prev){
      var next = {};
      for (var k in prev) if (Object.prototype.hasOwnProperty.call(prev, k)) next[k] = prev[k];
      next[row.id] = nextStatus;
      return next;
    });
    var body = { source: row.source, status: nextStatus };
    /* PR-K.19 — optional deny reason flows through to the PATCH endpoint
       and lands on doc.deniedReason for the queue + CSV. */
    if (extra.deniedReason) body.deniedReason = extra.deniedReason;
    fetch("/api/intake/queue/" + encodeURIComponent(row.id), {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body)
    })
      .then(function(r){
        return r.json().catch(function(){ return {}; }).then(function(b){
          return { status: r.status, body: b };
        });
      })
      .then(function(out){
        setBusy(function(prev){
          var next = {};
          for (var k in prev) if (k !== row.id) next[k] = prev[k];
          return next;
        });
        if (out.status >= 200 && out.status < 300) {
          showToast("Marked " + (row.email || row.id) + " as " + nextStatus + ".");
          load();
          return;
        }
        var msg = (out.body && out.body.error) || ("HTTP " + out.status);
        showToast("Update failed: " + msg);
      })
      .catch(function(e){
        setBusy(function(prev){
          var next = {};
          for (var k in prev) if (k !== row.id) next[k] = prev[k];
          return next;
        });
        showToast("Update failed: " + String((e && e.message) || e));
      });
  }

  /* ── DETAIL MODAL — open / fetch / save / close ── */
  function openDetail(row){
    /* Stage with what we already have so the modal renders instantly,
       then hydrate with the full doc (IP, UA, email outcomes, status
       timestamps) via GET /api/intake/queue/{id}?source=X. */
    setDetailRow({ id: row.id, source: row.source });
    setDetailNotes(row.notes || "");
    setDetailAssignee(row.assignee || "");
    setDetailScanSessionId(row.scanSessionId || "");
    setDetailEngagement(row.engagementType || "free");
    setDetailDispatchMode(row.dispatchMode || "manual");
    setDetailError(null);
    setDetailLoading(true);
    fetch("/api/intake/queue/" + encodeURIComponent(row.id) +
          "?source=" + encodeURIComponent(row.source),
          { credentials: "include" })
      .then(function(r){
        return r.json().catch(function(){ return {}; }).then(function(b){
          return { status: r.status, body: b };
        });
      })
      .then(function(out){
        setDetailLoading(false);
        if (out.status >= 200 && out.status < 300) {
          setDetailRow(out.body);
          /* Server values trump optimistic stage in case the queue list
             was slightly stale relative to the source-of-truth doc. */
          setDetailNotes(out.body.notes || "");
          setDetailAssignee(out.body.assignee || "");
          setDetailScanSessionId(out.body.scanSessionId || "");
          setDetailEngagement(out.body.engagementType || "free");
          setDetailDispatchMode(out.body.dispatchMode || "manual");
          return;
        }
        setDetailError((out.body && out.body.error) || ("HTTP " + out.status));
      })
      .catch(function(e){
        setDetailLoading(false);
        setDetailError(String((e && e.message) || e));
      });
  }

  function closeDetail(){
    if (detailSaving) return;
    setDetailRow(null);
    setDetailNotes("");
    setDetailAssignee("");
    setDetailError(null);
  }

  function saveDetail(){
    if (!detailRow || !detailRow.id) return;
    setDetailSaving(true);
    setDetailError(null);
    fetch("/api/intake/queue/" + encodeURIComponent(detailRow.id), {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        source: detailRow.source,
        notes: detailNotes,
        assignee: detailAssignee,
        scanSessionId: detailScanSessionId,
        engagementType: detailEngagement,
        dispatchMode: detailDispatchMode
      })
    })
      .then(function(r){
        return r.json().catch(function(){ return {}; }).then(function(b){
          return { status: r.status, body: b };
        });
      })
      .then(function(out){
        setDetailSaving(false);
        if (out.status >= 200 && out.status < 300) {
          showToast(out.body.updated ? "Saved." : "No changes to save.");
          setDetailRow(null);
          setDetailNotes("");
          setDetailAssignee("");
          load();
          return;
        }
        setDetailError((out.body && out.body.error) || ("HTTP " + out.status));
      })
      .catch(function(e){
        setDetailSaving(false);
        setDetailError(String((e && e.message) || e));
      });
  }

  /* PR-K.23 — preview state. Holds the row + files returned from a
     preview call so the admin can review before sending. */
  var s_previewFor = useState(null);
  var previewFor = s_previewFor[0], setPreviewFor = s_previewFor[1];

  /* PR-K.25 — shared confirm/prompt dialog state. Replaces native
     window.confirm and window.prompt with a branded Modal so
     dialogs match the admin UI, can be styled / dismissed by
     keyboard / theme consistently, and are actually exercisable in
     end-to-end automation (the extension's QA pass couldn't see
     native dialogs).

     Shape: null when nothing's open; otherwise an object with:
        title, body            — copy
        confirmLabel, cancelLabel — button text
        danger                 — red Confirm styling (for Deny etc.)
        isPrompt + placeholder + promptValue — text input variant
        onConfirm(promptValue) — fired when Confirm is clicked */
  var s_confirmDialog = useState(null);
  var confirmDialog = s_confirmDialog[0], setConfirmDialog = s_confirmDialog[1];

  function askConfirm(opts){
    setConfirmDialog({
      title: opts.title || "Confirm",
      body: opts.body || null,
      confirmLabel: opts.confirmLabel || "Confirm",
      cancelLabel: opts.cancelLabel || "Cancel",
      danger: !!opts.danger,
      isPrompt: false,
      onConfirm: opts.onConfirm || function(){}
    });
  }
  function askPrompt(opts){
    setConfirmDialog({
      title: opts.title || "Confirm",
      body: opts.body || null,
      confirmLabel: opts.confirmLabel || "Confirm",
      cancelLabel: opts.cancelLabel || "Cancel",
      danger: !!opts.danger,
      isPrompt: true,
      placeholder: opts.placeholder || "",
      promptValue: opts.defaultValue || "",
      promptLabel: opts.promptLabel || null,
      onConfirm: opts.onConfirm || function(){}
    });
  }
  function updatePromptValue(v){
    setConfirmDialog(function(prev){
      if (!prev) return prev;
      return Object.assign({}, prev, { promptValue: v });
    });
  }
  function runConfirm(){
    if (!confirmDialog) return;
    var cb = confirmDialog.onConfirm;
    var value = confirmDialog.isPrompt ? (confirmDialog.promptValue || "") : null;
    setConfirmDialog(null);
    /* Defer the callback by a tick so the dialog has time to unmount
       cleanly before any toast/navigation kicks in. */
    setTimeout(function(){ cb(value); }, 0);
  }

  /* PR-K.23 — fire the send-report endpoint in PREVIEW mode. Returns
     SAS URLs the admin can click to download + inspect before sending
     to the customer. No email, no status flip. */
  function previewReport(row){
    if (!row || !row.id || !row.scanSessionId) {
      showToast("Paste the scan session id in the detail modal first.");
      return;
    }
    setSending(function(prev){
      var n = {}; for (var k in prev) if (prev[k]) n[k] = true;
      n[row.id] = true; return n;
    });
    fetch("/api/intake/queue/" + encodeURIComponent(row.id) + "/send-report", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ source: row.source, previewOnly: true })
    })
      .then(function(r){
        return r.json().catch(function(){ return {}; }).then(function(b){
          return { status: r.status, body: b };
        });
      })
      .then(function(out){
        setSending(function(prev){
          var n = {}; for (var k in prev) if (k !== row.id && prev[k]) n[k] = true;
          return n;
        });
        if (out.status >= 200 && out.status < 300 && out.body && out.body.preview) {
          setPreviewFor({ row: row, files: out.body.files });
          load();
          return;
        }
        var reason = (out.body && (out.body.error || out.body.reason)) || ("HTTP " + out.status);
        showToast("Preview failed: " + reason);
      })
      .catch(function(e){
        setSending(function(prev){
          var n = {}; for (var k in prev) if (k !== row.id && prev[k]) n[k] = true;
          return n;
        });
        showToast("Preview failed: " + String((e && e.message) || e));
      });
  }

  /* PR-K.22 — fire the send-report endpoint for one row. */
  function sendReport(row){
    if (!row || !row.id || !row.scanSessionId) {
      showToast("Paste the scan session id in the detail modal first.");
      return;
    }
    askConfirm({
      title: "Send reports to customer?",
      body: "The customer at " + (row.email || row.id) +
            " will receive an email with two download links " +
            "(Remediation Report and Assessment Report). " +
            "Make sure you've previewed and inspected the files first.",
      confirmLabel: "Send reports",
      onConfirm: function(){ sendReportInternal(row); }
    });
  }

  function sendReportInternal(row){
    setSending(function(prev){
      var n = {}; for (var k in prev) if (prev[k]) n[k] = true;
      n[row.id] = true; return n;
    });
    fetch("/api/intake/queue/" + encodeURIComponent(row.id) + "/send-report", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ source: row.source })
    })
      .then(function(r){
        return r.json().catch(function(){ return {}; }).then(function(b){
          return { status: r.status, body: b };
        });
      })
      .then(function(out){
        setSending(function(prev){
          var n = {}; for (var k in prev) if (k !== row.id && prev[k]) n[k] = true;
          return n;
        });
        if (out.status >= 200 && out.status < 300 && out.body && out.body.sent) {
          showToast("Reports sent to " + (row.email || row.id) + ".");
          load();
          return;
        }
        var reason = (out.body && (out.body.error || out.body.reason)) || ("HTTP " + out.status);
        showToast("Report send failed: " + reason);
      })
      .catch(function(e){
        setSending(function(prev){
          var n = {}; for (var k in prev) if (k !== row.id && prev[k]) n[k] = true;
          return n;
        });
        showToast("Report send failed: " + String((e && e.message) || e));
      });
  }

  /* CSV export — same filters as the table, capped at 10k rows by the
     server. Streams through a blob so authenticated SWA cookies stay
     attached (the principal header would be stripped by a plain
     window.open / location-set, which would 401). */
  function downloadCsv(){
    if (csvBusy) return;
    setCsvBusy(true);
    var params = [];
    if (status) params.push("status=" + encodeURIComponent(status));
    if (source) params.push("source=" + encodeURIComponent(source));
    if (q)      params.push("q="      + encodeURIComponent(q));
    if (since)  params.push("since="  + encodeURIComponent(since));
    params.push("format=csv");
    var url = "/api/intake/queue?" + params.join("&");
    fetch(url, { credentials: "include" })
      .then(function(r){
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.blob();
      })
      .then(function(blob){
        var dlUrl = URL.createObjectURL(blob);
        var a = document.createElement("a");
        a.href = dlUrl;
        a.download = "intakes-" + new Date().toISOString().slice(0,10) + ".csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(dlUrl);
        setCsvBusy(false);
      })
      .catch(function(e){
        setCsvBusy(false);
        showToast("CSV download failed: " + String((e && e.message) || e));
      });
  }

  /* ── PR-K.21 BULK SELECT helpers ──
     selected = { rowId: true }. Idiomatic Set would be cleaner but the
     existing state shape elsewhere in this component uses plain objects
     for setState immutability tracking; matching that. */
  var selectedIds = Object.keys(selected).filter(function(k){ return selected[k]; });
  var selectedCount = selectedIds.length;
  var visibleIds = items.map(function(r){ return r.id; });
  var allVisibleSelected = visibleIds.length > 0 &&
    visibleIds.every(function(id){ return selected[id]; });
  function toggleRow(id){
    setSelected(function(prev){
      var n = {}; for (var k in prev) if (prev[k]) n[k] = true;
      if (n[id]) delete n[id]; else n[id] = true;
      return n;
    });
  }
  function toggleAllVisible(){
    setSelected(function(){
      if (allVisibleSelected) return {};
      var n = {};
      for (var i = 0; i < visibleIds.length; i++) n[visibleIds[i]] = true;
      return n;
    });
  }
  function clearSelection(){ setSelected({}); }

  /* Bulk PATCH — fires one PATCH per selected row in parallel. For
     soft-preview volumes (admin manually picking <100 rows) this is
     fast enough. Reuses the existing per-row PATCH endpoint so there's
     no new backend surface to authorize or audit separately. */
  function runBulkAction(targetStatus, opts){
    opts = opts || {};
    if (selectedCount === 0 || bulkBusy) return;
    var label = targetStatus === "approved" ? "Approve"
              : targetStatus === "denied"   ? "Deny"
              : "Mark as " + targetStatus;
    var rowsWord = " selected row" + (selectedCount === 1 ? "" : "s");

    /* Deny path needs a reason first — show a prompt dialog, then on
       confirm chain into the actual action with the reason captured. */
    if (targetStatus === "denied") {
      askPrompt({
        title: "Deny " + selectedCount + rowsWord + "?",
        body: "The same reason will be recorded on every selected intake.",
        promptLabel: "Reason for denial (optional — defaults to 'bulk_action')",
        placeholder: "bulk_action",
        defaultValue: "",
        confirmLabel: "Deny",
        danger: true,
        onConfirm: function(value){
          var deniedReason = (value || "").trim() || "bulk_action";
          executeBulkAction(targetStatus, deniedReason);
        }
      });
      return;
    }

    askConfirm({
      title: label + " " + selectedCount + rowsWord + "?",
      body: null,
      confirmLabel: label,
      danger: targetStatus === "archived",
      onConfirm: function(){ executeBulkAction(targetStatus, null); }
    });
  }

  function executeBulkAction(targetStatus, deniedReason){
    setBulkBusy(true);
    var rowsById = {};
    items.forEach(function(r){ rowsById[r.id] = r; });
    var jobs = selectedIds.map(function(id){
      var row = rowsById[id];
      if (!row) return Promise.resolve({ ok: false, id: id, reason: "row gone" });
      var body = { source: row.source, status: targetStatus };
      if (deniedReason) body.deniedReason = deniedReason;
      return fetch("/api/intake/queue/" + encodeURIComponent(id), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body)
      })
        .then(function(r){ return { ok: r.status >= 200 && r.status < 300, id: id, status: r.status }; })
        .catch(function(e){ return { ok: false, id: id, reason: String((e && e.message) || e) }; });
    });
    Promise.all(jobs).then(function(results){
      setBulkBusy(false);
      var okCount = results.filter(function(r){ return r.ok; }).length;
      var failCount = results.length - okCount;
      showToast(okCount + " updated" + (failCount ? ", " + failCount + " failed" : "") + ".");
      clearSelection();
      load();
    });
  }

  /* Sticky action bar — visible only when ≥1 row is selected.
     Position: bottom of viewport so it doesn't compete with the
     table header. */
  var bulkBar = selectedCount === 0 ? null : E("div", {
    role: "region",
    "aria-label": "Bulk actions",
    style: {
      position: "sticky", bottom: 16, zIndex: 30,
      margin: "12px 0",
      display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
      padding: "10px 14px",
      background: P.slate, color: "#FFFFFF",
      borderRadius: 10,
      boxShadow: "0 12px 32px rgba(0,0,0,0.18)"
    }
  },
    E("span", { style: { fontWeight: 800, fontSize: 13 } },
      selectedCount + " selected"),
    E("div", { style: { display: "flex", gap: 6, flexWrap: "wrap", marginLeft: "auto" } },
      E("button", {
        type: "button",
        disabled: bulkBusy,
        onClick: function(){ runBulkAction("approved"); },
        style: bulkBtnStyle({ primary: true, busy: bulkBusy })
      }, "Approve & Scan"),
      E("button", {
        type: "button",
        disabled: bulkBusy,
        onClick: function(){ runBulkAction("denied"); },
        style: bulkBtnStyle({ danger: true, busy: bulkBusy })
      }, "Deny"),
      E("button", {
        type: "button",
        disabled: bulkBusy,
        onClick: function(){ runBulkAction("contacted"); },
        style: bulkBtnStyle({ busy: bulkBusy })
      }, "Mark contacted"),
      E("button", {
        type: "button",
        disabled: bulkBusy,
        onClick: function(){ runBulkAction("archived"); },
        style: bulkBtnStyle({ busy: bulkBusy })
      }, "Archive"),
      E("button", {
        type: "button",
        disabled: bulkBusy,
        onClick: clearSelection,
        style: bulkBtnStyle({ ghost: true, busy: bulkBusy })
      }, "Clear")
    )
  );

  function bulkBtnStyle(opts){
    opts = opts || {};
    var base = {
      padding: "8px 14px", borderRadius: 8,
      fontSize: 12, fontWeight: 700,
      cursor: opts.busy ? "wait" : "pointer",
      opacity: opts.busy ? 0.6 : 1,
      border: "1px solid rgba(255,255,255,0.2)",
      background: "rgba(255,255,255,0.1)",
      color: "#FFFFFF",
      minHeight: 32
    };
    if (opts.primary) { base.background = "#FFFFFF"; base.color = P.slate; base.border = "none"; }
    if (opts.danger)  { base.background = "#DC2626"; base.color = "#FFFFFF"; base.border = "none"; }
    if (opts.ghost)   { base.background = "transparent"; base.border = "1px solid rgba(255,255,255,0.35)"; }
    return base;
  }

  /* ── STATUS COUNT BADGES ── clickable filter chips at the top of the
     queue. Active status gets a filled pill; the rest stay outlined. */
  var BADGE_DEFS = [
    { id: "pending",   label: "Pending",   variant: "amber"   },
    { id: "contacted", label: "Contacted", variant: "accent"  },
    { id: "scanned",   label: "Scanned",   variant: "pri"     },
    { id: "delivered", label: "Delivered", variant: "emerald" },
    { id: "archived",  label: "Archived",  variant: "slate"   }
  ];
  function variantColor(v){
    switch(v){
      case "amber":   return { bg: P.amberLt   || "#FEF3C7", fg: P.amberDk   || "#92400E" };
      case "accent":  return { bg: P.accentLt  || "#CFFAFE", fg: P.accentDk  || "#155E75" };
      case "pri":     return { bg: P.priLt     || "#E0E7FF", fg: P.priDk     || P.pri || "#3730A3" };
      case "emerald": return { bg: P.emeraldLt || "#D1FAE5", fg: P.emeraldDk || "#065F46" };
      case "slate":   return { bg: P.bgS       || "#F1F5F9", fg: P.slateM    || "#475569" };
      default:        return { bg: "#E2E8F0",                 fg: "#1F2937" };
    }
  }
  var badgeRow = E("div", {
    role: "group",
    "aria-label": "Filter by status",
    style: { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", padding: "4px 0 12px" }
  },
    BADGE_DEFS.map(function(b){
      var n = counts[b.id] || 0;
      var active = status === b.id;
      var col = variantColor(b.variant);
      return E("button", {
        key: b.id,
        type: "button",
        "aria-pressed": active ? "true" : "false",
        onClick: function(){
          /* Toggle: clicking the active badge clears the filter. */
          setStatus(active ? "" : b.id);
        },
        style: {
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "5px 12px", borderRadius: 999,
          border: active ? ("2px solid " + col.fg) : "1px solid " + P.bdr,
          background: active ? col.bg : P.card,
          color: active ? col.fg : P.slate,
          fontSize: 12, fontWeight: 700, lineHeight: 1.4,
          cursor: "pointer", minHeight: 32, whiteSpace: "nowrap"
        }
      },
        E("span", null, b.label),
        E("span", {
          style: {
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            minWidth: 22, height: 18, padding: "0 6px",
            borderRadius: 999, background: active ? col.fg : col.bg,
            color: active ? col.bg : col.fg,
            fontSize: 11, fontWeight: 800
          }
        }, String(n))
      );
    }),
    E("div", {
      style: { marginLeft: "auto", fontSize: 12, color: P.slateM, fontWeight: 600 }
    }, total + " " + (total === 1 ? "shown" : "shown"))
  );

  /* ── HEADER TOOLBAR ── */
  var toolbar = E("div", {
    style: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
      padding: "12px 0 16px", borderBottom: "1px solid " + P.bdr, marginBottom: 14 }
  },
    E("div", { style: { display: "flex", flexDirection: "column", gap: 4 } },
      E("div", { style: { fontSize: 11, fontWeight: 700, color: P.slateM, textTransform: "uppercase", letterSpacing: ".06em" } }, "Status"),
      E("select", {
        value: status,
        "aria-label": "Filter by status",
        onChange: function(e){ setStatus(e.target.value); },
        style: { padding: "6px 10px", borderRadius: 8, border: "1px solid " + P.bdr, fontSize: 12, fontWeight: 600, background: P.card, minHeight: 36 }
      }, STATUS_OPTIONS.map(function(o){ return E("option", { key: o.id || "all", value: o.id }, o.label); }))
    ),
    E("div", { style: { display: "flex", flexDirection: "column", gap: 4 } },
      E("div", { style: { fontSize: 11, fontWeight: 700, color: P.slateM, textTransform: "uppercase", letterSpacing: ".06em" } }, "Date"),
      E("select", {
        value: since,
        "aria-label": "Filter by submission date",
        onChange: function(e){ setSince(e.target.value); },
        style: { padding: "6px 10px", borderRadius: 8, border: "1px solid " + P.bdr, fontSize: 12, fontWeight: 600, background: P.card, minHeight: 36 }
      }, DATE_OPTIONS.map(function(o){ return E("option", { key: o.id || "all", value: o.id }, o.label); }))
    ),
    E("div", { style: { display: "flex", flexDirection: "column", gap: 4 } },
      E("div", { style: { fontSize: 11, fontWeight: 700, color: P.slateM, textTransform: "uppercase", letterSpacing: ".06em" } }, "Source"),
      E("select", {
        value: source,
        "aria-label": "Filter by source",
        onChange: function(e){ setSource(e.target.value); },
        style: { padding: "6px 10px", borderRadius: 8, border: "1px solid " + P.bdr, fontSize: 12, fontWeight: 600, background: P.card, minHeight: 36 }
      }, SOURCE_OPTIONS.map(function(o){ return E("option", { key: o.id || "all", value: o.id }, o.label); }))
    ),
    E("div", { style: { display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 180 } },
      E("div", { style: { fontSize: 11, fontWeight: 700, color: P.slateM, textTransform: "uppercase", letterSpacing: ".06em" } }, "Search"),
      E("input", {
        type: "search",
        value: q,
        "aria-label": "Search name, email, or domain",
        placeholder: "email, domain, or name…",
        onChange: function(e){ setQ(e.target.value); },
        style: { padding: "6px 10px", borderRadius: 8, border: "1px solid " + P.bdr, fontSize: 12, background: P.card, minHeight: 36 }
      })
    ),
    E("button", {
      type: "button",
      onClick: load,
      title: "Reload the queue",
      style: { padding: "8px 14px", borderRadius: 8, border: "1px solid " + P.bdr,
        background: P.card, color: P.slate, fontSize: 12, fontWeight: 700,
        cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, minHeight: 36, alignSelf: "flex-end" }
    }, E(Refresh, { size: 14 }), " Refresh"),
    E("button", {
      type: "button",
      onClick: downloadCsv,
      disabled: csvBusy,
      title: "Download the current filtered list as a CSV file",
      "aria-busy": csvBusy ? "true" : "false",
      style: { padding: "8px 14px", borderRadius: 8, border: "1px solid " + P.bdr,
        background: csvBusy ? P.bgS : P.card, color: P.slate, fontSize: 12, fontWeight: 700,
        cursor: csvBusy ? "wait" : "pointer", opacity: csvBusy ? 0.7 : 1,
        display: "inline-flex", alignItems: "center", gap: 6, minHeight: 36, alignSelf: "flex-end" }
    }, csvBusy ? "Downloading…" : "Download CSV")
  );

  if (loading) {
    return E("div", null,
      badgeRow,
      toolbar,
      E(Skeleton, { rows: 6, height: 20, label: "Loading intake queue…" })
    );
  }
  if (error) {
    return E("div", null,
      badgeRow,
      toolbar,
      E(ErrorState, { title: "Couldn't load the intake queue.",
        message: String(error), onRetry: load })
    );
  }
  if (!items.length) {
    return E("div", null,
      badgeRow,
      toolbar,
      E(EmptyState, {
        icon: E(Mail, { size: 32 }),
        title: status ? "No " + status + " intakes right now."
                      : "No active intakes yet.",
        message: "New free-scan submissions from paris.pi-consulting.us/free-scan and the Pi Consulting widget will land here. Refresh after a submission to see it appear."
      })
    );
  }

  /* Small KV row helper for the detail modal. Plain-string and React
     element values both work; null/empty renders as a dash. */
  function detailKV(label, value, opts){
    opts = opts || {};
    var v = (value === null || value === undefined || value === "") ? "—" : value;
    return E("div", { style: { marginBottom: 10 } },
      E("div", { style: { fontSize: 10, fontWeight: 700, color: P.slateM,
        textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 3 } }, label),
      E("div", { style: { fontSize: 13, color: P.slate,
        fontFamily: opts.mono ? FC : "inherit", wordBreak: "break-word" } }, v)
    );
  }

  function emailStatusEl(emailObj){
    if (emailObj && emailObj.sent) {
      var suffix = (typeof emailObj.recipientCount === "number" && emailObj.recipientCount > 0)
        ? " — " + emailObj.recipientCount + (emailObj.recipientCount === 1 ? " recipient" : " recipients")
        : "";
      /* PR-K.18: when ACS Event Grid has reported back, append the
         delivery outcome alongside the "Sent" tick so the admin sees
         the full lifecycle in one line. */
      var delivery = emailObj.deliveryStatus;
      if (delivery === "Delivered") {
        return E("span", null,
          E("span", { style: { color: "#059669", fontWeight: 700 } }, "Sent" + suffix),
          E("span", { style: { color: "#059669", fontWeight: 600, marginLeft: 6 } }, " · Delivered ✓"));
      }
      if (delivery && delivery !== "Expanded" && delivery !== "OutForDelivery") {
        return E("span", null,
          E("span", { style: { color: "#059669", fontWeight: 700 } }, "Sent" + suffix),
          E("span", { style: { color: "#991B1B", fontWeight: 700, marginLeft: 6 } }, " · " + delivery));
      }
      return E("span", { style: { color: "#059669", fontWeight: 700 } }, "Sent" + suffix);
    }
    var reason = emailObj && emailObj.reason ? " (" + emailObj.reason + ")" : "";
    return E("span", { style: { color: "#92400E", fontWeight: 600 } }, "Not sent" + reason);
  }

  var detailModal = !detailRow ? null : E(Modal, {
    open: !!detailRow,
    onClose: closeDetail,
    title: "Free scan submission",
    size: "lg",
    footer: E("div", { style: { display: "flex", gap: 8 } },
      E("button", {
        type: "button",
        onClick: closeDetail,
        disabled: detailSaving,
        style: { padding: "8px 16px", borderRadius: 8, border: "1px solid " + P.bdr,
          background: P.card, color: P.slate, fontSize: 12, fontWeight: 700,
          cursor: detailSaving ? "not-allowed" : "pointer", opacity: detailSaving ? 0.6 : 1, minHeight: 36 }
      }, "Cancel"),
      E("button", {
        type: "button",
        onClick: saveDetail,
        disabled: detailSaving,
        "aria-busy": detailSaving ? "true" : "false",
        style: { padding: "8px 18px", borderRadius: 8, border: "none",
          background: P.pri, color: "#FFF", fontSize: 12, fontWeight: 700,
          cursor: detailSaving ? "not-allowed" : "pointer", opacity: detailSaving ? 0.7 : 1, minHeight: 36 }
      }, detailSaving ? "Saving…" : "Save changes"))
  },
    detailLoading ? E("div", {
      style: { padding: "8px 12px", marginBottom: 12, background: P.bgS,
        borderRadius: 6, fontSize: 11, color: P.slateM, fontStyle: "italic" }
    }, "Loading full detail…") : null,

    detailError ? E("div", {
      role: "alert",
      style: { padding: "10px 12px", marginBottom: 12, background: "#FEE2E2",
        border: "1px solid #FCA5A5", borderRadius: 6, color: "#991B1B",
        fontSize: 12, fontWeight: 600 }
    }, String(detailError)) : null,

    E("div", { style: { display: "grid", gridTemplateColumns: "1fr 1fr",
      gap: 18, marginBottom: 6 } },
      /* LEFT — submission detail */
      E("section", { "aria-label": "Submission details" },
        E("h3", { style: { fontSize: 11, fontWeight: 800, color: P.slate,
          textTransform: "uppercase", letterSpacing: ".06em", margin: "0 0 10px" } },
          "Submission"),
        detailKV("Name", detailRow.name),
        detailKV("Email", detailRow.email
          ? E("a", { href: "mailto:" + detailRow.email,
              style: { color: P.pri, textDecoration: "none" } }, detailRow.email)
          : null),
        detailKV("Website", detailRow.domain
          ? E("a", { href: "https://" + detailRow.domain, target: "_blank", rel: "noopener noreferrer",
              style: { color: P.pri, textDecoration: "none" } }, detailRow.domain)
          : null, { mono: true }),
        detailKV("Source",
          detailRow.source === "paris" ? "PiARIS landing"
          : detailRow.source === "pi-consulting" ? "Pi Consulting site"
          : detailRow.source),
        detailKV("Submitted", detailRow.submittedAt
          ? new Date(detailRow.submittedAt).toLocaleString()
          : null),
        detailKV("Current status",
          E(Chip, { variant: STATUS_VARIANT[detailRow.status] || "default", size: "sm" },
            detailRow.status || "—"))
      ),
      /* RIGHT — delivery + origin */
      E("section", { "aria-label": "Email delivery and origin" },
        E("h3", { style: { fontSize: 11, fontWeight: 800, color: P.slate,
          textTransform: "uppercase", letterSpacing: ".06em", margin: "0 0 10px" } },
          "Email delivery"),
        detailKV("Customer ack", emailStatusEl(detailRow.ackEmail)),
        detailKV("Internal notify", emailStatusEl(detailRow.notifyEmail)),
        E("h3", { style: { fontSize: 11, fontWeight: 800, color: P.slate,
          textTransform: "uppercase", letterSpacing: ".06em", margin: "16px 0 10px" } },
          "Origin"),
        detailKV("IP address", detailRow.ip, { mono: true }),
        detailKV("User agent", detailRow.userAgent, { mono: true })
      )
    ),

    /* PR-K.19: risk + denial section, full-width below the
       Submission/Delivery grid. Only renders when there's
       something interesting to show — keeps clean rows uncluttered. */
    (detailRow.riskScore || (Array.isArray(detailRow.riskFlags) && detailRow.riskFlags.length > 0) || detailRow.deniedReason)
      ? E("section", {
          "aria-label": "Risk and denial",
          style: { borderTop: "1px solid " + P.bdr, paddingTop: 14, marginTop: 16 }
        },
          E("h3", { style: { fontSize: 11, fontWeight: 800, color: P.slate,
            textTransform: "uppercase", letterSpacing: ".06em", margin: "0 0 10px" } },
            "Risk + denial"),
          detailRow.riskScore
            ? detailKV("Risk score",
                detailRow.riskScore === "needs_review"
                  ? E("span", { style: { color: "#78350F", fontWeight: 700 } }, "⚠ Needs review")
                  : E("span", { style: { color: "#059669", fontWeight: 700 } }, "Clear ✓"))
            : null,
          (Array.isArray(detailRow.riskFlags) && detailRow.riskFlags.length > 0)
            ? detailKV("Flags",
                E("ul", { style: { margin: 0, paddingLeft: 18, listStyle: "disc" } },
                  detailRow.riskFlags.map(function(f, i) {
                    return E("li", { key: i, style: { fontSize: 12, color: P.slate, marginBottom: 2 } },
                      E("strong", null, f.flag),
                      f.detail ? " — " + f.detail : "");
                  })))
            : null,
          detailRow.deniedReason
            ? detailKV("Denial reason",
                E("span", { style: { color: "#991B1B", fontWeight: 600 } },
                  detailRow.deniedReason +
                  (detailRow.deniedBy ? " (by " + detailRow.deniedBy + ")" : "")))
            : null
        )
      : null,

    /* EDITABLE — assignee + notes */
    E("section", {
      "aria-label": "Triage",
      style: { borderTop: "1px solid " + P.bdr, paddingTop: 14, marginTop: 10 }
    },
      E("h3", { style: { fontSize: 11, fontWeight: 800, color: P.slate,
        textTransform: "uppercase", letterSpacing: ".06em", margin: "0 0 10px" } },
        "Triage"),
      /* PR-K.32: dispatch mode safety gate. Manual (default) keeps
         the intake invisible to the Phase C worker pool — admin runs
         the scan in PARIS themselves. Auto opts the row into the
         worker's claim query. Sits above Engagement because it's the
         coarser switch: priority only matters once the row is auto. */
      E("div", { style: { marginBottom: 14 } },
        E("div", { id: "iq-detail-dispatch-label",
          style: { display: "block", fontSize: 11, fontWeight: 700,
            color: P.slate, marginBottom: 5 } },
          "Dispatch mode"),
        E("div", {
          role: "radiogroup",
          "aria-labelledby": "iq-detail-dispatch-label",
          style: { display: "inline-flex", borderRadius: 8,
            border: "1px solid " + P.bdr, overflow: "hidden",
            background: P.card }
        },
          ["manual", "auto"].map(function(opt){
            var active = detailDispatchMode === opt;
            var label = opt === "auto" ? "Auto — Phase C worker" : "Manual — admin runs scan";
            return E("button", {
              key: opt,
              type: "button",
              role: "radio",
              "aria-checked": active ? "true" : "false",
              onClick: function(){ setDetailDispatchMode(opt); },
              style: {
                padding: "8px 14px", fontSize: 12, fontWeight: 700,
                cursor: "pointer", border: "none", minHeight: 36,
                background: active
                  ? (opt === "auto" ? "#DCFCE7" : P.pri)
                  : "transparent",
                color: active
                  ? (opt === "auto" ? "#166534" : "#FFFFFF")
                  : P.slateM,
                fontFamily: "inherit"
              }
            }, label);
          })
        ),
        E("div", { style: { fontSize: 11, color: P.slateM, marginTop: 6, lineHeight: 1.45 } },
          "Manual is the default: you run the scan in PiARIS, paste the session id, send the report — today's workflow. Auto opts this intake into the Phase C worker pool; flip it per-row until you trust the automated path end-to-end.")
      ),
      /* PR-K.28: engagement classification. Two-button segmented
         control reads cleaner than a dropdown for binary state, and
         the chosen value is highlighted with the same indigo/violet
         the queue row's PAID chip uses. */
      E("div", { style: { marginBottom: 14 } },
        E("div", { id: "iq-detail-engagement-label",
          style: { display: "block", fontSize: 11, fontWeight: 700,
            color: P.slate, marginBottom: 5 } },
          "Engagement"),
        E("div", {
          role: "radiogroup",
          "aria-labelledby": "iq-detail-engagement-label",
          style: { display: "inline-flex", borderRadius: 8,
            border: "1px solid " + P.bdr, overflow: "hidden",
            background: P.card }
        },
          ["free", "paid"].map(function(opt){
            var active = detailEngagement === opt;
            var label = opt === "paid" ? "Paid — priority dispatch" : "Free";
            return E("button", {
              key: opt,
              type: "button",
              role: "radio",
              "aria-checked": active ? "true" : "false",
              onClick: function(){ setDetailEngagement(opt); },
              style: {
                padding: "8px 14px", fontSize: 12, fontWeight: 700,
                cursor: "pointer", border: "none", minHeight: 36,
                background: active
                  ? (opt === "paid" ? "#EDE9FE" : P.pri)
                  : "transparent",
                color: active
                  ? (opt === "paid" ? "#5B21B6" : "#FFFFFF")
                  : P.slateM,
                fontFamily: "inherit"
              }
            }, label);
          })
        ),
        E("div", { style: { fontSize: 11, color: P.slateM, marginTop: 6, lineHeight: 1.45 } },
          "Paid engagements jump the Phase C worker queue ahead of any waiting free scans. They do NOT preempt a free scan already running.")
      ),
      E("div", { style: { marginBottom: 12 } },
        E("label", { htmlFor: "iq-detail-assignee",
          style: { display: "block", fontSize: 11, fontWeight: 700,
            color: P.slate, marginBottom: 5 } }, "Assignee (email)"),
        E("input", {
          id: "iq-detail-assignee",
          type: "email",
          value: detailAssignee,
          autoComplete: "email",
          onChange: function(e){ setDetailAssignee(e.target.value); },
          placeholder: "analyst@pi-consulting.us",
          style: { width: "100%", padding: "8px 10px", borderRadius: 6,
            border: "1px solid " + P.bdr, fontSize: 12, background: P.card,
            minHeight: 36, fontFamily: "inherit" }
        })
      ),
      /* PR-K.22 — scan session linkage. Admin pastes this after running
         the scan in PARIS so the Send Report action knows which session
         to render. Format-tolerant; the backend trims + caps at 120 chars. */
      E("div", { style: { marginBottom: 12 } },
        E("label", { htmlFor: "iq-detail-scan-session",
          style: { display: "block", fontSize: 11, fontWeight: 700,
            color: P.slate, marginBottom: 5 } },
          "Scan session ID ",
          E("span", { style: { fontWeight: 400, color: P.slateM } }, "(paste from PiARIS after running the scan)")),
        E("input", {
          id: "iq-detail-scan-session",
          type: "text",
          value: detailScanSessionId,
          autoComplete: "off",
          spellCheck: "false",
          onChange: function(e){ setDetailScanSessionId(e.target.value); },
          placeholder: "sess-abc123…",
          style: { width: "100%", padding: "8px 10px", borderRadius: 6,
            border: "1px solid " + P.bdr, fontSize: 12, background: P.card,
            minHeight: 36, fontFamily: FC }
        })
      ),
      E("div", null,
        E("label", { htmlFor: "iq-detail-notes",
          style: { display: "block", fontSize: 11, fontWeight: 700,
            color: P.slate, marginBottom: 5 } }, "Internal notes"),
        E("textarea", {
          id: "iq-detail-notes",
          value: detailNotes,
          onChange: function(e){ setDetailNotes(e.target.value); },
          placeholder: "Free-form notes — scope, blockers, hand-off context…",
          style: { width: "100%", minHeight: 110, padding: "8px 10px",
            borderRadius: 6, border: "1px solid " + P.bdr, fontSize: 12,
            background: P.card, fontFamily: "inherit", resize: "vertical" }
        })
      )
    )
  );

  return E("div", null,
    badgeRow,
    toolbar,
    E("table", {
      style: { width: "100%", borderCollapse: "collapse", fontSize: 12 }
    },
      E("thead", null,
        E("tr", null,
          /* PR-K.21: bulk-select header — clicking the master
             checkbox toggles all currently-visible rows. */
          E("th", { scope: "col",
            style: { padding: "10px 8px 10px 12px", textAlign: "left",
              background: P.bgS, borderBottom: "1px solid " + P.bdr, width: 28 }
          },
            E("input", {
              type: "checkbox",
              checked: allVisibleSelected,
              "aria-label": "Select all visible rows",
              onChange: toggleAllVisible,
              style: { cursor: "pointer", width: 16, height: 16 }
            })
          ),
          ["Submitted", "Contact", "Domain", "Source", "Status", "Actions"].map(function(h){
            return E("th", { key: h, scope: "col",
              style: { padding: "10px 12px", textAlign: "left",
                background: P.bgS, borderBottom: "1px solid " + P.bdr,
                fontWeight: 700, color: P.slateM, fontSize: 11,
                textTransform: "uppercase", letterSpacing: ".04em" } }, h);
          }))),
      E("tbody", null,
        items.map(function(row, i){
          var rowBusy = !!busy[row.id];
          var rowSelected = !!selected[row.id];
          return E("tr", { key: row.id, style: { background: rowSelected ? "#EEF2FF" : (i % 2 === 0 ? P.card : "#FAFBFE") } },
            E("td", { style: { padding: "10px 8px 10px 12px",
              borderBottom: "1px solid " + P.bdr, verticalAlign: "top" } },
              E("input", {
                type: "checkbox",
                checked: rowSelected,
                "aria-label": "Select " + (row.email || row.id),
                onChange: function(){ toggleRow(row.id); },
                onClick: function(e){ e.stopPropagation(); },
                style: { cursor: "pointer", width: 16, height: 16 }
              })
            ),
            E("td", { style: { padding: "10px 12px", borderBottom: "1px solid " + P.bdr,
              color: P.slateM, fontFamily: FC, fontSize: 11, whiteSpace: "nowrap", verticalAlign: "top" } },
              fmtWhen(row.submittedAt)),
            E("td", { style: { padding: "10px 12px", borderBottom: "1px solid " + P.bdr, verticalAlign: "top" } },
              E("button", {
                type: "button",
                onClick: function(){ openDetail(row); },
                "aria-label": "Open details for " + (row.name || row.email),
                style: { background: "none", border: "none", padding: 0,
                  textAlign: "left", cursor: "pointer", font: "inherit", color: "inherit" }
              },
                E("div", { style: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" } },
                  E("div", { style: { fontWeight: 700, color: P.pri } }, row.name || "(no name)"),
                  /* PR-K.10: TEST chip flags submissions classified by the
                     PR-K.9 rules (internal @pi-consulting.us email OR RFC
                     2606 reserved domain). null = legacy pre-PR-K.9 row
                     where the field wasn't set — hide the chip. */
                  row.testMode === true
                    ? E("span", {
                        title: "Classified as TEST by PR-K.9 rules — internal staff email or RFC 2606 reserved domain",
                        style: { display: "inline-flex", alignItems: "center",
                          padding: "1px 7px", borderRadius: 999,
                          background: "#FEF3C7", color: "#78350F",
                          fontSize: 9, fontWeight: 800, letterSpacing: ".06em",
                          textTransform: "uppercase", whiteSpace: "nowrap" }
                      }, "TEST")
                    : null,
                  /* PR-K.18: BOUNCED chip when ACS Event Grid reported the
                     customer ack as bounced/failed/quarantined/suppressed.
                     null = no report yet (or pre-K.18 doc); only render
                     for genuine non-delivery outcomes. */
                  (row.ackDeliveryStatus &&
                   row.ackDeliveryStatus !== "Delivered" &&
                   row.ackDeliveryStatus !== "Expanded" &&
                   row.ackDeliveryStatus !== "OutForDelivery")
                    ? E("span", {
                        title: "ACS reported the customer ack email as " + row.ackDeliveryStatus + " — the submitter probably never received it",
                        style: { display: "inline-flex", alignItems: "center",
                          padding: "1px 7px", borderRadius: 999,
                          background: "#FEE2E2", color: "#991B1B",
                          fontSize: 9, fontWeight: 800, letterSpacing: ".06em",
                          textTransform: "uppercase", whiteSpace: "nowrap" }
                      }, row.ackDeliveryStatus.toUpperCase())
                    : null,
                  /* PR-K.19: NEEDS REVIEW chip when one or more risk
                     flags are present. Hover tooltip lists the actual
                     flags so admin sees at a glance why the row is
                     suspicious. Hidden when the row is already in
                     denied/archived state (decision already made). */
                  (row.riskScore === "needs_review" &&
                   row.status !== "denied" && row.status !== "archived")
                    ? E("span", {
                        title: "Risk flags: " + (row.riskFlags || []).map(function(f){
                          return f.flag + (f.detail ? " (" + f.detail + ")" : "");
                        }).join(", "),
                        style: { display: "inline-flex", alignItems: "center",
                          padding: "1px 7px", borderRadius: 999,
                          background: "#FEF3C7", color: "#78350F",
                          fontSize: 9, fontWeight: 800, letterSpacing: ".06em",
                          textTransform: "uppercase", whiteSpace: "nowrap" }
                      }, "⚠ Review")
                    : null,
                  /* PR-K.19: AUTO-DENIED chip distinguishes domains we
                     auto-rejected (restricted_domain, etc.) from
                     domains an admin manually denied. */
                  (row.status === "denied" && row.deniedBy === "auto")
                    ? E("span", {
                        title: "Auto-denied: " + (row.deniedReason || "restricted"),
                        style: { display: "inline-flex", alignItems: "center",
                          padding: "1px 7px", borderRadius: 999,
                          background: "#7F1D1D", color: "#FFFFFF",
                          fontSize: 9, fontWeight: 800, letterSpacing: ".06em",
                          textTransform: "uppercase", whiteSpace: "nowrap" }
                      }, "AUTO-DENIED")
                    : null,
                  /* PR-K.28: PAID chip — engagement type that gets
                     priority dispatch from the Phase C worker pool.
                     Hidden for free (the default and majority case). */
                  row.engagementType === "paid"
                    ? E("span", {
                        title: "Paid engagement — priority dispatch (rank " + (row.priorityRank != null ? row.priorityRank : 0) + ")",
                        style: { display: "inline-flex", alignItems: "center",
                          padding: "1px 7px", borderRadius: 999,
                          background: "#EDE9FE", color: "#5B21B6",
                          fontSize: 9, fontWeight: 800, letterSpacing: ".06em",
                          textTransform: "uppercase", whiteSpace: "nowrap" }
                      }, "PAID")
                    : null,
                  /* PR-K.32: AUTO-SCAN chip — this row is opted into
                     the Phase C worker pool's claim query. Hidden when
                     dispatchMode is manual (the default), because
                     "this scan is operator-driven" is the assumed
                     state and decorating every row would be noise. */
                  row.dispatchMode === "auto"
                    ? E("span", {
                        title: "Auto-scan: claimable by the Phase C worker pool",
                        style: { display: "inline-flex", alignItems: "center",
                          padding: "1px 7px", borderRadius: 999,
                          background: "#DCFCE7", color: "#166534",
                          fontSize: 9, fontWeight: 800, letterSpacing: ".06em",
                          textTransform: "uppercase", whiteSpace: "nowrap" }
                      }, "AUTO-SCAN")
                    : null
                ),
                E("div", { style: { fontSize: 11, color: P.slateM, fontFamily: FC, marginTop: 2 } }, row.email))),
            E("td", { style: { padding: "10px 12px", borderBottom: "1px solid " + P.bdr,
              fontFamily: FC, fontSize: 11, verticalAlign: "top" } }, row.domain || "—"),
            E("td", { style: { padding: "10px 12px", borderBottom: "1px solid " + P.bdr,
              fontSize: 11, color: P.slateM, verticalAlign: "top" } },
              row.source === "paris" ? "PiARIS" : row.source === "pi-consulting" ? "Pi Consulting" : row.source),
            E("td", { style: { padding: "10px 12px", borderBottom: "1px solid " + P.bdr, verticalAlign: "top" } },
              E(Chip, { variant: STATUS_VARIANT[row.status] || "default", size: "sm" }, row.status || "—"),
              row.ackEmailSent
                ? null
                : E("div", { style: { fontSize: 10, color: P.slateM, marginTop: 4, fontStyle: "italic" } },
                    "ack email not sent")),
            E("td", { style: { padding: "10px 12px", borderBottom: "1px solid " + P.bdr, verticalAlign: "top" } },
              E("div", { style: { display: "flex", gap: 6, flexWrap: "wrap" } },
                (NEXT_ACTIONS[row.status] || []).map(function(a){
                  /* PR-K.19: primary actions (Approve & Scan) get the
                     filled indigo treatment; danger actions (Deny) get
                     red. The danger path prompts for a reason so the
                     queue + CSV record WHY the admin denied. */
                  var btnStyle = {
                    padding: "4px 10px", borderRadius: 6,
                    fontSize: 11, fontWeight: 700,
                    cursor: rowBusy ? "not-allowed" : "pointer",
                    opacity: rowBusy ? 0.6 : 1, minHeight: 28,
                    border: "1px solid " + P.bdr,
                    background: P.card, color: P.slate
                  };
                  if (a.primary) {
                    btnStyle.background = P.pri;
                    btnStyle.color = "#FFFFFF";
                    btnStyle.border = "none";
                  } else if (a.danger) {
                    btnStyle.background = "#FEE2E2";
                    btnStyle.color = "#991B1B";
                    btnStyle.border = "1px solid #FCA5A5";
                  }
                  return E("button", {
                    key: a.status,
                    type: "button",
                    disabled: rowBusy,
                    onClick: function(){
                      if (a.prompt) {
                        /* PR-K.25: branded Modal prompt instead of
                           native window.prompt. */
                        askPrompt({
                          title: "Deny intake from " + (row.email || row.id),
                          body: null,
                          promptLabel: a.prompt,
                          placeholder: "Reason (optional)",
                          defaultValue: "",
                          confirmLabel: "Deny",
                          danger: true,
                          onConfirm: function(value){
                            patchStatus(row, a.status, { deniedReason: (value || "").trim() });
                          }
                        });
                      } else {
                        patchStatus(row, a.status);
                      }
                    },
                    style: btnStyle
                  }, a.label);
                }),
                /* PR-K.22/K.23: PREVIEW + SEND REPORT — only when
                   status allows AND the scan session id is set on the
                   row. Preview generates the files without emailing
                   so the admin can inspect first. Send fires the email
                   afterwards. Both indigo to read as primary completion
                   actions, but Preview is outlined to read as the
                   safer/inspection step. */
                (SEND_REPORT_STATUSES[row.status] && row.scanSessionId)
                  ? E("button", {
                      type: "button",
                      key: "prev-" + row.id,
                      disabled: !!sending[row.id],
                      onClick: function(){ previewReport(row); },
                      title: "Generate the reports without emailing the customer — admin downloads + inspects first",
                      style: { padding: "4px 10px", borderRadius: 6,
                        border: "1px solid " + P.pri, background: P.card, color: P.pri,
                        fontSize: 11, fontWeight: 700,
                        cursor: sending[row.id] ? "wait" : "pointer",
                        opacity: sending[row.id] ? 0.7 : 1, minHeight: 28 }
                    }, "Preview")
                  : null,
                (SEND_REPORT_STATUSES[row.status] && row.scanSessionId)
                  ? E("button", {
                      type: "button",
                      key: "send-" + row.id,
                      disabled: !!sending[row.id],
                      onClick: function(){ sendReport(row); },
                      style: { padding: "4px 10px", borderRadius: 6,
                        border: "none", background: P.pri, color: "#FFFFFF",
                        fontSize: 11, fontWeight: 700,
                        cursor: sending[row.id] ? "wait" : "pointer",
                        opacity: sending[row.id] ? 0.7 : 1, minHeight: 28 }
                    }, sending[row.id] ? "Sending…" : "Send Report")
                  : null,
                E("a", {
                  href: buildMailto(row),
                  title: "Open your mail client with a pre-filled message",
                  style: { padding: "4px 10px", borderRadius: 6,
                    border: "1px solid " + P.bdr, background: P.card, color: P.pri,
                    fontSize: 11, fontWeight: 600, cursor: "pointer", minHeight: 28,
                    textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }
                }, E(Mail, { size: 12 }), " Email")
              ))
          );
        }))),
    bulkBar,
    detailModal,
    /* PR-K.25: branded confirm/prompt dialog — replaces native
       window.confirm and window.prompt. Reuses the existing Modal
       component (focus trap, Esc-to-close, focus restore on close). */
    confirmDialog ? E(Modal, {
      open: true,
      onClose: function(){ setConfirmDialog(null); },
      title: confirmDialog.title,
      size: "sm",
      footer: E("div", { style: { display: "flex", gap: 8 } },
        E("button", {
          type: "button",
          onClick: function(){ setConfirmDialog(null); },
          style: { padding: "8px 16px", borderRadius: 8,
            border: "1px solid " + P.bdr, background: P.card, color: P.slate,
            fontSize: 12, fontWeight: 700, cursor: "pointer", minHeight: 36 }
        }, confirmDialog.cancelLabel),
        E("button", {
          type: "button",
          onClick: runConfirm,
          style: { padding: "8px 18px", borderRadius: 8, border: "none",
            background: confirmDialog.danger ? "#DC2626" : P.pri,
            color: "#FFFFFF", fontSize: 12, fontWeight: 800,
            cursor: "pointer", minHeight: 36 }
        }, confirmDialog.confirmLabel))
    },
      confirmDialog.body
        ? E("p", { style: { margin: "0 0 12px", fontSize: 13, color: P.slate, lineHeight: 1.5 } },
            confirmDialog.body)
        : null,
      confirmDialog.isPrompt
        ? E("div", null,
            confirmDialog.promptLabel
              ? E("label", {
                  htmlFor: "iq-confirm-prompt",
                  style: { display: "block", fontSize: 11, fontWeight: 700, color: P.slate, marginBottom: 5 }
                }, confirmDialog.promptLabel)
              : null,
            E("input", {
              id: "iq-confirm-prompt",
              type: "text",
              autoFocus: true,
              value: confirmDialog.promptValue || "",
              placeholder: confirmDialog.placeholder || "",
              onChange: function(e){ updatePromptValue(e.target.value); },
              onKeyDown: function(e){ if (e.key === "Enter") runConfirm(); },
              style: { width: "100%", padding: "8px 10px", borderRadius: 6,
                border: "1px solid " + P.bdr, fontSize: 13, background: P.card,
                minHeight: 36, fontFamily: "inherit", boxSizing: "border-box" }
            }))
        : null
    ) : null,
    /* PR-K.23: report preview modal. Shown after a successful Preview
       click — admin can download each file, then either Send to
       Customer (fires the real send-report POST) or Close. */
    previewFor ? E(Modal, {
      open: !!previewFor,
      onClose: function(){ setPreviewFor(null); },
      title: "Preview reports for " + (previewFor.row.email || previewFor.row.domain),
      size: "md",
      footer: E("div", { style: { display: "flex", gap: 8, width: "100%", justifyContent: "space-between" } },
        E("div", { style: { fontSize: 11, color: P.slateM, alignSelf: "center" } },
          "Inspect each file before sending. The customer won't receive anything yet."),
        E("div", { style: { display: "flex", gap: 8 } },
          E("button", {
            type: "button",
            onClick: function(){ setPreviewFor(null); },
            style: { padding: "8px 16px", borderRadius: 8, border: "1px solid " + P.bdr,
              background: P.card, color: P.slate, fontSize: 12, fontWeight: 700,
              cursor: "pointer", minHeight: 36 }
          }, "Close"),
          E("button", {
            type: "button",
            onClick: function(){
              var row = previewFor.row;
              setPreviewFor(null);
              sendReport(row);
            },
            style: { padding: "8px 18px", borderRadius: 8, border: "none",
              background: P.pri, color: "#FFF", fontSize: 12, fontWeight: 800,
              cursor: "pointer", minHeight: 36 }
          }, "Send to customer")
        )
      )
    },
      E("div", { style: { padding: "0 0 6px", fontSize: 13, color: P.slate, lineHeight: 1.5 } },
        "These are the actual files that will be emailed when you click Send to customer. Download each one, open in Excel/Word, and verify the data is correct and complete before sending."),
      E("div", { style: { marginTop: 16, display: "flex", flexDirection: "column", gap: 10 } },
        (previewFor.files || []).map(function(f){
          return E("a", {
            key: f.type,
            href: f.sasUrl,
            target: "_blank",
            rel: "noopener noreferrer",
            download: f.filename,
            style: { display: "flex", alignItems: "center", gap: 10,
              padding: "12px 14px", borderRadius: 8,
              border: "1px solid " + P.bdr, background: P.bgS,
              textDecoration: "none", color: P.slate }
          },
            E("span", { "aria-hidden": "true", style: {
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 36, height: 36, borderRadius: 8,
              background: P.pri, color: "#FFF",
              fontSize: 11, fontWeight: 800, textTransform: "uppercase"
            }}, f.ext),
            E("div", { style: { flex: 1, minWidth: 0 } },
              E("div", { style: { fontWeight: 700, fontSize: 13 } }, f.label),
              E("div", { style: { fontSize: 11, color: P.slateM, fontFamily: FC, marginTop: 2, wordBreak: "break-all" } }, f.filename)
            ),
            E("span", { style: { fontSize: 11, fontWeight: 700, color: P.pri } }, "Download →")
          );
        })
      ),
      E("div", {
        style: { marginTop: 16, padding: "10px 12px", background: "#FEF3C7",
          border: "1px solid #F59E0B", borderRadius: 6, fontSize: 11,
          color: "#78350F", lineHeight: 1.5 }
      },
        E("strong", null, "Hallucination guarantee: "),
        "These reports were generated with AI narrative disabled. Every issue, every WCAG criterion, and every page URL in these files came directly from the scan session — nothing was generated or invented by an LLM.")
    ) : null
  );
}
